/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Tag,
  Badge,
  Tooltip,
  Button,
  Col,
  Input,
  Select,
  Modal,
  Popover,
  Space,
} from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  MessageOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { parseISO } from "date-fns";
import axios, { AxiosError, AxiosResponse } from "axios";
import { Link } from "react-router-dom";
import { Filters, FiltersCard } from "../../components/Filters";
import { Label } from "../../components/ui";

import api from "../../services/api";

import { useToast } from "../../hooks/toast";
import { useAuth } from "../../hooks/auth";
// eslint-disable-next-line import/extensions
import Table from "../../components/Table";
import UserAvatar from "../../components/UserAvatar";
import UserModal from "./UserModal";
import { Container } from "./styles";
import SendMessageModal from "./SendMessageModal";
import UserRoles from "../../enums/UserRoles";

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

export interface IUser {
  id: string;
  name: string;
  email: string;
  phone_number: number;
  avatar_url: string;
  monthly_productive_hours: number;
  hiring_model: "CORPORATE" | "SHAREHOLDER" | "CLT" | "CLT_TEMP";
  role: UserRoles;
  seniority: "JUNIOR" | "FULL" | "SENIOR";
  status: "ACTIVE" | "INACTIVE";
  admission_date: string;
  dismissal_date: string;
  skills: {
    id: string;
    name: string;
  }[];

  allocation?: [];
  factory?: [];
  model?: "FACTORY" | "ALLOCATION"
}

export interface ISkill {
  id: string;
  name: string;
}

const Users: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [sendMessageModalVisible, setSendMessageModalVisible] = useState<
    boolean
  >(false);
  const [editedUser, setEditedUser] = useState<IUser | undefined>();
  const [loadingSkills, setLoadingSkills] = useState<boolean>(false);
  const [skills, setSkills] = useState<ISkill[]>();
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [managers, setManagers] = useState<IUser[]>([]);

  const { addToast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation(["users"]);

  // Filters
  const [name, setName] = useState<string | undefined>();
  const [nameValue, setNameValue] = useState<string | undefined>("");
  const [email, setEmail] = useState<string | undefined>();
  const [emailValue, setEmailValue] = useState<string | undefined>("");
  const [seniority, setSeniority] = useState();
  const [role, setRole] = useState();
  const [hiringModel, setHiringModel] = useState();
  const [skillsFilters, setSkillsFilters] = useState();
  const [managerFilter, setManagerFilter] = useState();

  const isLoggedUserManager = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role]
  );

  const skillsOptions = useMemo(() => {
    if (!skills) {
      return undefined;
    }

    return (
      skills
        // eslint-disable-next-line no-nested-ternary
        .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
        .map((skill) => ({
          label: skill.name,
          value: skill.id,
        }))
    );
  }, [skills]);

  const managerOptions = useMemo(() => {
    if (!managers) {
      return undefined;
    }

    return managers.map((manager) => ({
      label: manager.name,
      value: manager.id,
    }));
  }, [managers]);

  const searchParams = useMemo(() => {
    return {
      name,
      email,
      role,
      hiring_model: hiringModel,
      seniority,
      skills_ids: skillsFilters,
      manager_id: managerFilter,
    };
  }, [name, email, role, hiringModel, seniority, skillsFilters, managerFilter]);

  useEffect(() => {
    setLoadingManagers(true);
    api
      .get<IUser[]>("/users", {
        params: {
          role: "MANAGER",
        },
      })
      .then((response) => {
        setManagers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingManagers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoadingSkills(true);
    api
      .get<IUser[]>("/skills")
      .then((response) => {
        setSkills(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro ao buscar os skills",
          description: "Occoreu um erro ao buscar os skills, tente novamente.",
        });
      })
      .finally(() => setLoadingSkills(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined, isDeleting?: boolean) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 401:
          description = "Token inválido ou faltando";
          break;
        case 404:
          description = "O usuário não foi encontrado";
          break;
        default:
          description = isDeleting
            ? "Ocorreu um erro ao excluir o usuário, tente novamente"
            : "Ocorreu um erro ao buscar os usuários, tente novamente";
      }

      addToast({
        type: "error",
        title: isDeleting ? "Erro na exclusão" : "Erro na busca",
        description,
      });
    },
    [addToast]
  );

  useEffect(() => {
    setLoading(true);
    api
      .get<IUser[]>("/users", {
        params: searchParams,
      })
      .then((response) => {
        setUsers(response.data);
      })
      .catch((err) => {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleDeleteUser = useCallback(
    (userId: string) => {
      api
        .delete(`/users/${userId}`)
        .then(() => {
          addToast({
            type: "success",
            title: "Usuário excluido",
            description: "O usuário foi excluido com sucesso",
          });

          setUsers((oldUsers) => [
            ...oldUsers.filter((usr) => usr.id !== userId),
          ]);
        })
        .catch((err) => {
          if (axios.isAxiosError(err)) {
            const serverError = err as AxiosError;
            generateErrorToast(serverError.response);
          }
        });
    },
    [addToast, generateErrorToast]
  );

  /*  const handleEditUser = useCallback((user: IUser) => {
    setEditedUser(user);
    setModalVisible(true);
  }, []); */

  const handleSendMessage = useCallback((user: IUser) => {
    setEditedUser(user);
    setSendMessageModalVisible(true);
  }, []);

  const showDelConfirm = useCallback(
    (userId, userName) => {
      confirm({
        title: t("deleteModal.title"),
        icon: <ExclamationCircleOutlined />,
        content: t("deleteModal.alert", { name: userName }),
        centered: true,
        okText: t("deleteModal.yes"),
        cancelText: t("deleteModal.no"),
        okButtonProps: { type: "primary", danger: true },
        onOk: () => handleDeleteUser(userId),
      });
    },
    [t, handleDeleteUser]
  );

  const columns: ColumnsType<IUser> = [
    {
      title: "",
      dataIndex: "avatar_url",
      render: (url: string, user: IUser) => {
        return <UserAvatar name={user.name} url={user.avatar_url} size={36} />;
      },
    },
    {
      title: t("name"),
      dataIndex: "name",
    },
    {
      title: t("email"),
      dataIndex: "email",
    },
    {
      title: t("hiring_model"),
      dataIndex: "hiring_model",
      render: (hiring_model: string) => {
        return (
          <Tag
            color={t(`hiring_models.${hiring_model}.color`)}
            key={hiring_model}
          >
            {t(`hiring_models.${hiring_model}.label`)}
          </Tag>
        );
      },
    },
    {
      title: "Número de telefone",
      dataIndex: "phone_number",
    },
    {
      title: "Horas produtivas mês",
      dataIndex: "monthly_productive_hours",
    },
    {
      title: t("role"),
      dataIndex: "role",
      render: (role: string) => {
        return (
          <Tag color={t(`roles.${role}.color`)} key={role}>
            {t(`roles.${role}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("seniority"),
      dataIndex: "seniority",
      render: (seniority: string) => {
        return (
          <Tag color={t(`seniorities.${seniority}.color`)} key={seniority}>
            {t(`seniorities.${seniority}.label`)}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, record) => (
        <Tooltip
          title={`${
            record.admission_date
              ? parseISO(
                  record.admission_date.split("T")[0]
                ).toLocaleDateString("pt-BR")
              : ""
          } - ${
            record.dismissal_date
              ? parseISO(
                  record.dismissal_date.split("T")[0]
                ).toLocaleDateString("pt-BR")
              : ""
          }`}
        >
          <Badge
            size="default"
            status={status === "ACTIVE" ? "success" : "error"}
          />
        </Tooltip>
      ),
    },
    {
      title: "Gestor",
      dataIndex: "manager",
      render: (manager: IUser) => {
        if (manager) {
          return (
            <UserAvatar
              name={manager.name}
              url={manager.avatar_url}
              size={36}
            />
          );
        }
        return <></>;
      },
    },
    {
      title: t("skills"),
      dataIndex: "skills",
      render: (skills: { id: string; name: string }[]) => {
        if (skills.length > 2) {
          const displayed = skills.slice(0, 2);
          const others = skills.slice(2);

          return (
            <>
              {displayed.map((skill) => (
                <Tag color="orange" key={skill.id}>
                  {skill.name}
                </Tag>
              ))}
              <Popover
                placement="bottomRight"
                content={() => (
                  <Space size="small" direction="vertical">
                    {others.map((skill) => (
                      <Tag color="orange" key={skill.id}>
                        {skill.name}
                      </Tag>
                    ))}
                  </Space>
                )}
                trigger="hover"
              >
                <Tag color="orange">{`+${others.length}`}</Tag>
              </Popover>
            </>
          );
        }

        return skills.map((skill) => (
          <Tag color="orange" key={skill.id}>
            {skill.name}
          </Tag>
        ));
      },
    },
    {
      title: "Ações",
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Button
            icon={<MessageOutlined />}
            type="link"
            onClick={() => handleSendMessage(record)}
          />
          { isLoggedUserManager && <Link to={`/app/users/${record.id}`}>
            <FileSearchOutlined />
          </Link>}
          {/* <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => handleEditUser(record)}
          /> */}
          <Button
            icon={<DeleteOutlined />}
            type="link"
            danger
            onClick={() => showDelConfirm(record.id, record.name)}
          />
        </>
      ),
    },
  ];

  const handleShowFilters = useCallback(
    (value) => {
      setShowFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showFilters) {
        setName(undefined);
        setEmail(undefined);
        setRole(undefined);
        setSeniority(undefined);
        setHiringModel(undefined);
        setSkillsFilters(undefined);
        setManagerFilter(undefined);
      }
    },
    [showFilters]
  );

  const handleCloseModal = useCallback(
    (user?: IUser) => {
      setEditedUser(undefined);
      if (user) {
        const index = users.findIndex((usr) => usr.id === user.id);
        const newUsers = users;

        if (index > -1) {
          newUsers[index] = user;
        } else {
          newUsers.push(user);
        }
        setUsers([...newUsers]);
      }
      setModalVisible(false);
      setSendMessageModalVisible(false);
    },
    [users]
  );

  const extraActions = useMemo(
    () =>
      user.role === UserRoles.Manager ? (
        <Tooltip title={t("actions.create")}>
          <Button
            icon={<PlusCircleOutlined />}
            type="link"
            size="large"
            onClick={() => setModalVisible(true)}
          />
        </Tooltip>
      ) : (
        <></>
      ),
    [isLoggedUserManager, t]
  );

  const userRolesOptions = useMemo(() => {
    return (Object.keys(UserRoles) as (keyof typeof UserRoles)[]).map(
      (key, _) => {
        return {
          label: t(`roles.${UserRoles[key]}.label`),
          value: UserRoles[key],
        };
      }
    );
  }, [t]);

  const extraFilters = useMemo(
    () =>
      showFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("role")}</Label>
            </div>
            <Select
              placeholder={t("role")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              onChange={(value) => setRole(value)}
              options={userRolesOptions}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("seniority")}</Label>
            </div>
            <Select
              placeholder={t("seniority")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              onChange={(value) => setSeniority(value)}
            >
              <Option value="JUNIOR">{t("seniorities.JUNIOR.label")}</Option>
              <Option value="FULL">{t("seniorities.FULL.label")}</Option>
              <Option value="SENIOR">{t("seniorities.SENIOR.label")}</Option>
            </Select>
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("hiring_model")}</Label>
            </div>
            <Select
              placeholder={t("hiring_model")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              onChange={(value) => setHiringModel(value)}
            >
              <Option value="CLT">{t("hiring_models.CLT.label")}</Option>
              <Option value="CLT_TEMP">
                {t("hiring_models.CLT_TEMP.label")}
              </Option>
              <Option value="SHAREHOLDER">
                {t("hiring_models.SHAREHOLDER.label")}
              </Option>
              <Option value="CORPORATE">
                {t("hiring_models.CORPORATE.label")}
              </Option>
            </Select>
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("name")}</Label>
            </div>
            <Search
              placeholder={t("name")}
              onSearch={(value) => {
                setName(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setNameValue(value || undefined);
              }}
              value={nameValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("email")}</Label>
            </div>
            <Search
              placeholder={t("email")}
              onSearch={(value) => {
                setEmail(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setEmailValue(value || undefined);
              }}
              value={emailValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("skills")}</Label>
            </div>
            <Select
              placeholder={t("skills")}
              style={{ width: "100%" }}
              mode="multiple"
              options={skillsOptions}
              loading={loadingSkills}
              allowClear
              showSearch
              onChange={(value) => setSkillsFilters(value)}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("manager")}</Label>
            </div>
            <Select
              allowClear
              options={managerOptions}
              placeholder={t("manager")}
              loading={loadingManagers}
              style={{ width: "100%" }}
              onChange={(value) => setManagerFilter(value)}
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [
      emailValue,
      loadingManagers,
      loadingSkills,
      managerOptions,
      nameValue,
      showFilters,
      skillsOptions,
      t,
    ]
  );

  return (
    <Container>
      <UserModal
        visible={modalVisible}
        handleCloseModal={handleCloseModal}
        user={editedUser}
      />
      <SendMessageModal
        visible={sendMessageModalVisible}
        handleCloseModal={handleCloseModal}
        user={editedUser}
      />
      <Table
        data={users}
        columns={columns}
        title={
          <>
            Usuários
            <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            />
          </>
        }
        extraActions={extraActions}
        footer={() => <></>}
        loading={loading}
        pagination={false}
        extraFilters={extraFilters}
      />
    </Container>
  );
};

export default Users;
